import { jsPDF, AcroFormTextField, AcroFormCheckBox } from 'jspdf';

// Every downloaded PDF gets real form fields: "Label: value" lines become text boxes, small squares become checkboxes.
const LABEL_VALUE = /^([A-Za-z#][^:\n]{0,42}):\s+(.{0,90})$/;
const MARK = /^\s*[Xx✓✔]\s*$/;
const PT = 0.3528;
let nextMode = null;
let lastMode = null;
let seq = 0;
const FLAT = new Set(['flat', 'print']);

export const setNextFillMode = (mode) => { nextMode = mode; };
export const takeFillMode = () => { const m = nextMode; nextMode = null; lastMode = m; return m; };
export const isFlatMode = (m) => FLAT.has(m);

// Patient shown on generated PDFs: the PDF's own patient, completed from the last chart opened if it is the same person
let pdfPatient = null;
const usDob = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v || ''); };
const normPatient = (p) => {
  if (!p) return {};
  const name = p.name || p.patientName || [p.firstName, p.lastName].filter(Boolean).join(' ');
  const code = p.primaryDiagnosisCode || p.primaryIcd10 || '';
  const dx = p.dx || p.primaryDiagnosis || '';
  return { name, dob: usDob(p.dob || p.dateOfBirth), mrn: p.mrn || p.medicalRecordNumber || '',
    dx: code && !String(dx).includes(code) ? `${code} ${dx}`.trim() : dx,
    admission: usDob(p.admission || p.startOfCareDate || p.socDate || p.admissionDate || p.admitDate) };
};
export const setPdfPatient = (p) => { pdfPatient = p ? normPatient(p) : null; if (p?.id) chartsById.set(p.id, p); };
const chartsById = new Map();
export const cachePdfPatients = (list = []) => list.forEach(p => p?.id && chartsById.set(p.id, p));
const sameName = (a, b) => String(a || '').toLowerCase().replace(/[^a-z]/g, '') === String(b || '').toLowerCase().replace(/[^a-z]/g, '');
export const resolvePdfPatient = (own) => {
  const mine = normPatient(own);
  const byId = own?.patientId && chartsById.get(own.patientId);
  if (byId) {  // the PDF names its chart: fill gaps from it, its agency MR# wins
    const c = normPatient(byId);
    return { ...c, ...Object.fromEntries(Object.entries(mine).filter(([key, v]) => v && key !== 'mrn')) };
  }
  const chart = pdfPatient || {};
  if (!mine.name) return {};  // never borrow another chart's details
  if (!sameName(mine.name, chart.name)) return mine;
  // the chart's agency MR# always wins
  return { ...mine, ...Object.fromEntries(Object.entries(chart).filter(([key, v]) => v && (key === 'mrn' || !mine[key]))) };
};
// true once after a PDF was built in 'print' mode: the caller prints instead of downloading
export const takePrintRequest = () => { const p = lastMode === 'print'; lastMode = null; return p; };
export const printBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.setAttribute('data-testid', 'print-frame');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  frame.onload = () => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (e) { window.open(url, '_blank'); } };
  frame.src = url;
  document.body.appendChild(frame);
  setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 120000);
};
export const peekFillMode = () => nextMode;
// same shape, every value emptied (for blank downloads)
export const blankCopy = (v) => (Array.isArray(v) ? [] : v && typeof v === 'object' ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, blankCopy(x)])) : typeof v === 'boolean' ? false : typeof v === 'number' ? '' : typeof v === 'string' ? '' : v);

export const chooseFillMode = () => new Promise((resolve) => {
  const wrap = document.createElement('div');
  wrap.setAttribute('data-testid', 'fill-mode-dialog');
  wrap.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:10000;display:flex;align-items:center;justify-content:center;padding:16px;font-family:Arial,sans-serif';
  wrap.innerHTML = `<div style="background:#fff;border-radius:10px;max-width:420px;width:100%;overflow:hidden">
    <div style="background:#111;color:#d4af37;padding:12px 18px;font-weight:bold">Download PDF</div>
    <div style="padding:18px;font-size:14px;color:#333">Editable and blank copies let nurses type in every box and tick every checkbox. The filled PDF is a finished, read-only copy.</div>
    <div style="display:flex;flex-direction:column;gap:8px;padding:0 18px 18px">
      <button data-mode="prefill" data-testid="fill-mode-prefill" style="padding:10px;border-radius:6px;border:0;background:#d4af37;font-weight:bold;cursor:pointer">Pre-filled (editable)</button>
      <button data-mode="blank" data-testid="fill-mode-blank" style="padding:10px;border-radius:6px;border:1px solid #999;background:#fff;font-weight:bold;cursor:pointer">Blank (fillable)</button>
      <button data-mode="flat" data-testid="fill-mode-flat" style="padding:10px;border-radius:6px;border:1px solid #1e40af;background:#fff;color:#1e40af;font-weight:bold;cursor:pointer">Filled PDF (read-only)</button>
      <button data-mode="print" data-testid="fill-mode-print" style="padding:10px;border-radius:6px;border:1px solid #999;background:#f3f4f6;font-weight:bold;cursor:pointer">Print</button>
      <button data-mode="" data-testid="fill-mode-cancel" style="padding:8px;border:0;background:none;color:#666;cursor:pointer">Cancel</button>
    </div></div>`;
  const done = (m) => { wrap.remove(); document.removeEventListener('keydown', esc); resolve(m || null); };
  const esc = (e) => { if (e.key === 'Escape') done(null); };
  wrap.addEventListener('click', (e) => { const b = e.target.closest('button'); if (b) done(b.dataset.mode); else if (e.target === wrap) done(null); });
  document.addEventListener('keydown', esc);
  document.body.appendChild(wrap);
});

export const fillablePdf = (opts) => {
  const doc = new jsPDF(opts);
  const k = (72 / 25.4) / doc.internal.scaleFactor; // user units per mm
  const fields = [];
  const boxes = [];
  const page = () => doc.getCurrentPageInfo().pageNumber;
  const text = doc.text.bind(doc);
  const rect = doc.rect.bind(doc);
  const rectFilled = (x, y, w, h) => w >= 2.4 * k && w <= 7 * k && Math.abs(w - h) < 0.8 * k;
  const line = doc.line.bind(doc);
  const placed = [];
  const rules = [];
  const note = (t, x, y, o) => {
    if (typeof t !== 'string' || typeof x !== 'number' || !t.trim()) return;
    const w = doc.getTextWidth(t);
    const left = o?.align === 'center' ? x - w / 2 : o?.align === 'right' ? x - w : x;
    const bold = /bold/i.test(doc.getFont().fontStyle || '');
    placed.push({ page: page(), x: left, y, w, t: t.trim(), heading: bold && t.trim().length < 45 && t.trim() === t.trim().toUpperCase() && /[A-Z]{2}/.test(t) && !o?.align });
  };
  doc.line = (x1, y1, x2, y2, ...rest) => {
    if (doc.autoFill && Math.abs(y1 - y2) < 0.01 && Math.abs(x2 - x1) >= 30 * k) rules.push({ page: page(), x: Math.min(x1, x2), x2: Math.max(x1, x2), y: y1 });
    return line(x1, y1, x2, y2, ...rest);
  };
  let finalized = false;

  // explicit field registration for generators that place their own boxes
  doc.addFill = (f) => { fields.push({ page: page(), shade: false, ...f }); return doc; };
  doc.autoFill = true;

  doc.text = (t, x, y, o, ...rest) => {
    if (doc.valueMode && typeof x === 'number' && (typeof t === 'string' || Array.isArray(t))) {
      const lines = Array.isArray(t) ? t : [t];
      const fsu = doc.getFontSize() * PT * k;
      const w = Math.max(...lines.map(l => doc.getTextWidth(String(l))), 40 * k) + 4 * k;
      const maxX = doc.valueMaxX ?? doc.internal.pageSize.getWidth() - 10 * k;
      fields.push({ type: 'text', page: page(), x: x - 0.5 * k, y: y - fsu * 0.9, w: Math.min(w, maxX - x), h: Math.max(lines.length * fsu * 1.15 + 1.2 * k, 4.5 * k), value: lines.join(lines.length > 1 ? '\n' : ''), multiline: lines.length > 1, fontSize: doc.getFontSize() });
      return doc;
    }
    if (doc.autoFill && typeof t === 'string' && typeof x === 'number' && !(o && o.align && o.align !== 'left')) {
      if (MARK.test(t)) {
        const b = boxes.find(b => b.page === page() && x >= b.x - 1.5 && x <= b.x + b.w + 1.5 && y >= b.y - 1 && y <= b.y + b.h + 3);
        if (b) { b.on = true; return doc; }
      }
      const m = t.match(LABEL_VALUE);
      if (m && !/^https?$/i.test(m[1])) {
        const label = `${m[1]}:`;
        note(label, x, y, o);
        text(label, x, y, o, ...rest);
        const fs = (doc.getFontSize() * PT) * k;
        const fx = x + doc.getTextWidth(`${label} `);
        const val = m[2].replace(/_{2,}/g, '').trim();
        const w = Math.min(Math.max(doc.getTextWidth(val) + 4 * k, 42 * k), doc.internal.pageSize.getWidth() - 10 * k - fx);
        if (w > 12 * k) {
          fields.push({ type: 'text', page: page(), x: fx, y: y - fs * 0.85, w, h: Math.max(fs * 1.25, 4.2 * k), value: val, fontSize: doc.getFontSize() });
          return doc;
        }
      }
    }
    if (doc.autoFill) (Array.isArray(t) ? t : [t]).forEach((ln, i) => note(ln, x, y + i * doc.getFontSize() * PT * k * 1.15, o));
    return text(t, x, y, o, ...rest);
  };

  doc.rect = (x, y, w, h, style, ...rest) => {
    if (doc.autoFill && rectFilled(x, y, w, h)) {
      // a filled small square is a ticked box: keep only the outline, the checkbox shows the tick
      const on = style === 'F' || style === 'FD' || style === 'DF';
      const same = boxes.find(b => b.page === page() && Math.abs(b.x - x) < 1.5 * k && Math.abs(b.y - y) < 1.5 * k);
      if (same) { same.on = same.on || on; } else boxes.push({ page: page(), x, y, w, h, on });
      return on ? (style === 'F' ? doc : rect(x, y, w, h, 'S', ...rest)) : rect(x, y, w, h, style, ...rest);
    }
    return rect(x, y, w, h, style, ...rest);
  };

  const layout = () => {
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const occupied = (pg, x1, y1, x2, y2) => placed.some(p => p.page === pg && p.y > y1 && p.y - 2 * k < y2 && p.x < x2 && p.x + p.w > x1)
      || fields.some(f => f.page === pg && f.y < y2 && f.y + f.h > y1 && f.x < x2 && f.x + f.w > x1);
    // text boxes stop before the next word on the same line
    fields.filter(f => f.type === 'text' && !f.name).forEach(f => {
      const base = f.y + f.h * 0.7;
      const next = placed.filter(p => p.page === f.page && Math.abs(p.y - base) < 2.5 * k && p.x > f.x + 1).map(p => p.x);
      const nf = fields.filter(o => o !== f && o.page === f.page && Math.abs(o.y - f.y) < 2.5 * k && o.x > f.x + 1).map(o => o.x - doc.getTextWidth('xx:'));
      const limit = Math.min(pw - 10 * k, ...next, ...nf) - 2 * k;
      f.w = Math.max(10 * k, Math.min(f.w, limit - f.x));
    });
    // empty space under a section heading becomes a writing area
    placed.filter(h => h.heading).forEach(h => {
      const below = [...placed.filter(p => p.page === h.page && p.y > h.y + 1).map(p => p.y - doc.getFontSize() * PT * k),
        ...rules.filter(r => r.page === h.page && r.y > h.y).map(r => r.y - 6 * k)];
      const top = h.y + 2 * k;
      const bottom = Math.min(ph - 15 * k, ...below) - 2 * k;
      if (bottom - top >= 8 * k && !occupied(h.page, h.x, top, pw - 15 * k, bottom)) {
        fields.push({ type: 'text', page: h.page, x: h.x, y: top, w: pw - 15 * k - h.x, h: bottom - top, value: '', multiline: true, fontSize: 9 });
      }
    });
    // blank signature/date rules get a box just above them
    rules.forEach(r => {
      if (!occupied(r.page, r.x, r.y - 6.5 * k, r.x2, r.y - 0.3 * k)) fields.push({ type: 'text', page: r.page, x: r.x, y: r.y - 6.5 * k, w: r.x2 - r.x, h: 6 * k, value: '', fontSize: 10 });
    });
  };

  // read-only copy: values are printed as text, ticks as marks, no form fields
  const flatten = () => {
    const clean = (v) => String(v ?? '').replace(/[\u2013\u2014]/g, '-').replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"').replace(/[^\x20-\x7E\u00A0-\u00FF\n]/g, '');
    fields.forEach(f => {
      if (f.type === 'check') { if (f.value) boxes.push({ page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, on: true }); return; }
      const val = clean(f.value).trim();
      if (!val) return;
      doc.setPage(f.page);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(0);
      const fs = f.fontSize || 9;
      doc.setFontSize(fs);
      const lh = fs * PT * k * 1.15;
      const lines = doc.splitTextToSize(val, Math.max(f.w - 1.5 * k, 5 * k));
      const many = f.multiline || lines.length > 1;
      const shown = many ? lines.slice(0, Math.max(1, Math.floor((f.h + 0.5 * k) / lh))) : lines;
      shown.forEach((ln, i) => text(ln, f.x + 0.8 * k, many ? f.y + lh * (i + 0.85) : f.y + f.h * 0.72));
    });
    boxes.forEach(b => {
      if (!b.on) return;
      doc.setPage(b.page);
      doc.setDrawColor(0); doc.setLineWidth(0.35 * k);
      line(b.x + b.w * 0.2, b.y + b.h * 0.55, b.x + b.w * 0.42, b.y + b.h * 0.8);
      line(b.x + b.w * 0.42, b.y + b.h * 0.8, b.x + b.w * 0.82, b.y + b.h * 0.2);
    });
  };

  // Patient / DOB / MR# / Admission line (no diagnosis) at the foot of every page (labels only on blank copies)
  const stampPatient = (blank) => {
    const info = blank ? {} : resolvePdfPatient(doc.patientInfo);
    const lineText = `Patient: ${info.name || ''}   |   DOB: ${info.dob || ''}   |   MR#: ${info.mrn || ''}   |   Admission: ${info.admission || ''}`;
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const cur = page();
    for (let i = 1; i <= doc.getNumberOfPages(); i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 90);
      let fs = 7;
      doc.setFontSize(fs);
      while (doc.getTextWidth(lineText) > W - 20 * k && fs > 4.5) { fs -= 0.25; doc.setFontSize(fs); }
      text(lineText, 10 * k, H - 2.6 * k);
    }
    doc.setTextColor(0);
    doc.setPage(cur);
  };

  const finalize = (mode) => {
    if (finalized) return;
    finalized = true;
    if (doc.autoFill) layout();
    if (!doc.noPatientLine) stampPatient(mode === 'blank');
    const cur = page();
    const blank = mode === 'blank';
    if (FLAT.has(mode)) { flatten(); doc.setPage(cur); return; }
    [...fields, ...boxes.map(b => ({ type: 'check', ...b, value: b.on, shade: false }))].forEach(f => {
      doc.setPage(f.page);
      seq += 1;
      if (f.shade !== false) {
        doc.setFillColor(237, 244, 255); rect(f.x, f.y, f.w, f.h, 'F');
        doc.setDrawColor(150, 170, 200); doc.setLineWidth(0.2 * k); line(f.x, f.y + f.h, f.x + f.w, f.y + f.h);
      }
      if (f.type === 'check') {
        const c = new AcroFormCheckBox();
        c.fieldName = f.name || `check_${seq}`;
        Object.assign(c, { x: f.x, y: f.y, width: f.w, height: f.h });
        const on = !blank && Boolean(f.value);
        c.value = on ? 'On' : 'Off';
        c.appearanceState = on ? 'On' : 'Off';
        doc.addField(c);
      } else {
        const t = new AcroFormTextField();
        t.fieldName = f.name || `field_${seq}`;
        Object.assign(t, { x: f.x, y: f.y, width: f.w, height: f.h, fontSize: f.fontSize || 9, multiline: Boolean(f.multiline) });
        // jsPDF's appearance stream drops a leading '(' / trailing ')', so pad those with a space
        t.value = blank ? '' : String(f.value ?? '').replace(/[\u2013\u2014]/g, '-').replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"').replace(/^\(/, ' (').replace(/\)$/, ') ');
        if (f.align) t.textAlign = f.align;
        if (f.maxLength) t.maxLength = f.maxLength;
        doc.addField(t);
      }
    });
    doc.setPage(cur);
  };

  const save = doc.save.bind(doc);
  const output = doc.output.bind(doc);
  doc.finishBlob = (mode) => { finalize(mode); return output('blob'); };
  const deliver = (mode, name, rest) => {
    finalize(mode);
    if (mode === 'print') printBlob(output('blob')); else save(name, ...rest);
  };
  doc.save = (name, ...rest) => {
    const preset = doc.fillMode || takeFillMode();
    if (preset) { deliver(preset, name, rest); return doc; }
    chooseFillMode().then(mode => { if (mode) deliver(mode, name, rest); });
    return doc;
  };
  doc.output = (type, ...rest) => {
    lastMode = nextMode || doc.fillMode || 'prefill';
    finalize(lastMode);
    nextMode = null;
    return output(type, ...rest);
  };
  return doc;
};

const SKIP_TAGS = new Set(['BUTTON', 'SCRIPT', 'STYLE', 'SVG', 'NAV', 'NOSCRIPT', 'IMG', 'path']);
const TEXT_INPUT = /^(text|date|number|email|tel|time|datetime-local|month|search|url|)$/;

// Any on-screen form -> fillable PDF: text in place, every input becomes a field
export const exportFillableFromDom = async (root, filename = 'form.pdf') => {
  if (!root) return;
  const mode = await chooseFillMode();
  if (!mode) return;
  const doc = fillablePdf({ unit: 'pt', format: 'letter' });
  doc.autoFill = false;
  const M = 30;
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight() - 2 * M;
  const hidden = (el) => el.closest('[data-no-pdf], .print\\:hidden, nav, header, aside, [role="dialog"]') || !el.getClientRects().length;
  // fit the area actually used by the form, not the padded page
  const used = [...root.querySelectorAll('input, textarea, select, label, p, span, h1, h2, h3, h4, td, th')].filter(el => !hidden(el)).map(el => el.getBoundingClientRect()).filter(r => r.width > 0);
  const rb = root.getBoundingClientRect();
  const left = used.length ? Math.min(...used.map(r => r.left)) : rb.left;
  const right = used.length ? Math.max(...used.map(r => r.right)) : rb.right;
  const top = used.length ? Math.min(...used.map(r => r.top)) : rb.top;
  const box = { left, top, width: right - left };
  const s = Math.min(0.8, (PW - 2 * M) / Math.max(box.width, 1));
  const place = (r) => {
    const y = (r.top - box.top) * s;
    const pageIdx = Math.floor(y / PH);
    while (doc.getNumberOfPages() <= pageIdx) doc.addPage();
    doc.setPage(pageIdx + 1);
    return { x: M + (r.left - box.left) * s, y: M + (y - pageIdx * PH), w: r.width * s, h: r.height * s };
  };
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const el = n.parentElement;
    const t = n.textContent.replace(/\s+/g, ' ').trim();
    if (!t || !el || SKIP_TAGS.has(el.tagName) || el.closest('button, select, textarea, option, svg') || hidden(el)) continue;
    const range = document.createRange();
    range.selectNodeContents(n);
    const rects = [...range.getClientRects()].filter(r => r.width > 0);
    if (!rects.length) continue;
    const cs = getComputedStyle(el);
    const fs = Math.max(6, Math.min(18, parseFloat(cs.fontSize) * s));
    doc.setFont('helvetica', parseInt(cs.fontWeight, 10) >= 600 ? 'bold' : 'normal');
    doc.setFontSize(fs);
    const rgb = (cs.color.match(/\d+/g) || [0, 0, 0]).slice(0, 3).map(Number);
    const clean = t.replace(/[^\x20-\x7E\u00A0-\u00FF]/g, '');
    const lines = rects.length === 1 ? [clean] : doc.splitTextToSize(clean, Math.max(...rects.map(r => r.width)) * s + 2);
    lines.forEach((ln, i) => {
      const p = place(rects[Math.min(i, rects.length - 1)]);
      doc.setFont('helvetica', parseInt(cs.fontWeight, 10) >= 600 ? 'bold' : 'normal');
      doc.setFontSize(fs);
      doc.setTextColor(...(rgb.every(v => v > 230) ? [0, 0, 0] : rgb));
      doc.text(ln, p.x, p.y + fs * 0.85 + Math.max(0, i - rects.length + 1) * fs * 1.15);
    });
  }
  root.querySelectorAll('input, textarea, select').forEach((el, i) => {
    if (hidden(el) || el.type === 'hidden' || el.type === 'file') return;
    const p = place(el.getBoundingClientRect());
    if (p.w < 2) return;
    const name = `${(el.name || el.id || el.getAttribute('data-testid') || 'field').replace(/[^\w-]/g, '_')}_${i}`;
    if (el.type === 'checkbox' || el.type === 'radio') {
      const sz = Math.max(7, Math.min(p.w, p.h, 12));
      doc.setDrawColor(60); doc.setLineWidth(0.6); doc.rect(p.x, p.y, sz, sz);
      doc.addFill({ type: 'check', x: p.x, y: p.y, w: sz, h: sz, value: el.checked, name });
    } else if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || TEXT_INPUT.test(el.type || '')) {
      const raw = el.tagName === 'SELECT' ? (el.value ? el.selectedOptions[0]?.textContent || '' : '') : el.value;
      const value = el.type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw.slice(5, 7)}/${raw.slice(8, 10)}/${raw.slice(0, 4)}` : raw;
      doc.addFill({ type: 'text', x: p.x, y: p.y, w: p.w, h: Math.max(p.h, 10), value, name, shade: true, multiline: el.tagName === 'TEXTAREA', fontSize: Math.max(7, Math.min(10, p.h * 0.55)) });
    }
  });
  doc.fillMode = mode;
  doc.save(filename);
};
